import { chainOrSuspend, pyCallOrSuspend, pyStr } from "@Sk";
import { AnvilHookSpec, Component, ComponentConstructor, DropZone } from "@runtime/components/Component";
import { s_add_component, s_remove_from_parent, s_slot } from "@runtime/runner/py-util";
import { editState } from "../edit-state";
import { BASE_MODULES } from "../python-environment";

const { DesignHtmlTemplate, HtmlTemplate } = BASE_MODULES["anvil"];

const mkDropZone = (slotName: string, minChildIdx?: number, maxChildIdx?: number) => {
    const element = document.createElement("div");
    element.setAttribute("anvil-html-template-dropzone", "true");
    element.style.alignSelf = "stretch";
    return {
        element,
        dropInfo: {
            minChildIdx,
            maxChildIdx,
            layout_properties: { slot: slotName },
            slots_set_layout_properties: ["slot"],
        },
    };
};

const DesignerHooks: Partial<AnvilHookSpec> = {
    enableDropMode(this: Component, dropping, flags) {
        const self = this;
        const onlyMine = (elts: HTMLElement[]) =>
            [...elts].filter((e) => e.closest(".anvil-container") === self._anvil.domNode);
        const queryMine = (elt: HTMLElement, selector: string) =>
            onlyMine([...elt.querySelectorAll(selector)] as HTMLElement[])[0];
        const dzs: DropZone[] = [];
        const explicitDzs = new Map<string, HTMLElement>(); // Slot names
        const onlySlot = dropping.pyLayoutProperties?.quick$lookup(s_slot)?.toString();

        // This is a very specific hack to avoid generating automatic drop zones in old template apps:
        // if you use anvil-drop-default=, that means you're probably using explicit drop zones, so we can
        // avoid creating implicit ones anywhere.
        const isOldStyle = onlyMine(self._anvil.element.find("[anvil-drop-default]")).length !== 0;

        for (const element of onlyMine(self._anvil.element.find("[anvil-drop-slot]"))) {
            const slot = element.getAttribute("anvil-drop-slot")!;
            if (onlySlot && onlySlot !== slot) {
                continue;
            }
            // Don't use the drop zone if it's a conditional drop zone that hides when it's full.
            if (!element.classList.contains("anvil-force-hidden")) {
                explicitDzs.set(slot, element);
            }
            dzs.push({
                element,
                dropInfo: {
                    layout_properties: { slot },
                },
            });
        }

        // Create an map of component:index, so we don't have to look this up for every slot.
        const componentIndices = new Map<Component, number>();
        for (let i = 0; i < self._anvil.components.length; i++) {
            componentIndices.set(self._anvil.components[i].component, i);
        }

        for (const [name, slot] of Object.entries(self._anvil.slots)) {
            let minChildIdx = 0;

            if (onlySlot && onlySlot !== name) {
                continue;
            }
            const { element, repeat, components } = slot as {
                element: HTMLElement;
                repeat?: boolean;
                components: { component: Component; carrierElement: Element }[];
            };
            if (flags?.asComponent && element.hasAttribute("anvil-slot-internal")) {
                continue;
            }

            if (!repeat) {
                // Simple version; they've all been append()ed next to each other in the div, so we can generate
                // drop zones straightforwardly

                const findDropHereDz = () => {
                    const dropHere = queryMine(
                        element,
                        "[anvil-drop-here]:not(.anvil-force-hidden, .anvil-slot *, .anvil-slot-repeat *, .anvil-slot-repeated *)"
                    );
                    if (dropHere) {
                        const dz = {
                            element: dropHere as HTMLElement,
                            dropInfo: {
                                layout_properties: { slot: name },
                            },
                        };
                        dzs.push(dz);
                        return dz;
                    }
                };

                const explicitDz = explicitDzs.get(name) || findDropHereDz();

                if (!explicitDz) {
                    if (isOldStyle && components.length !== 0) {
                        continue;
                    }

                    // This is a straightforward [anvil-slot] with no explicit DZ
                    for (const { component } of components) {
                        const idx = componentIndices.get(component);
                        const preDz = mkDropZone(name, minChildIdx, idx);
                        minChildIdx = idx! + 1;
                        dzs.push(preDz);
                        component.anvil$hooks.domElement!.insertAdjacentElement("beforebegin", preDz.element);
                    }
                    const endDz = mkDropZone(name, minChildIdx);
                    dzs.push(endDz);
                    element.insertAdjacentElement("beforeend", endDz.element);
                } else {
                    // There is an explicit DZ for this slot. It will probably take care of showing/hiding itself,
                    // so don't add any implicit DZs.

                    // However, we do need to make sure that if we're currently dragging the only component
                    // in this slot, we can put it back down again. Create DZ out of the component itself (ew?).
                    // TODO this needs to be doable without access to editState
                    const onlyComponentName =
                        components.length === 1 && editState.namesByComponent.get(components[0].component);
                    if (dropping.dragging?.length === 1 && dropping.dragging[0].name === onlyComponentName) {
                        const childIdx = componentIndices.get(components[0].component);
                        dzs.push({
                            element,
                            dropInfo: {
                                minChildIdx,
                                maxChildIdx: childIdx,
                                layout_properties: { slot: name },
                                slots_set_layout_properties: ["slot"],
                            },
                        });
                        minChildIdx = childIdx! + 1;
                    }
                }
            } else {
                for (const { component, carrierElement } of components) {
                    const repeated = component.anvil$hooks.domElement!.closest(`[anvil-slot-repeated="${name}"]`)!;
                    const explicitPreDzElement = queryMine(repeated as HTMLElement, "[anvil-drop-here]");
                    const idx = componentIndices.get(component);
                    if (explicitPreDzElement) {
                        dzs.push({
                            element: explicitPreDzElement,
                            dropInfo: {
                                minChildIdx,
                                maxChildIdx: idx,
                                layout_properties: { slot: name },
                                slots_set_layout_properties: ["slot"],
                            },
                        });
                    } else if (!isOldStyle) {
                        const preDz = mkDropZone(name, minChildIdx, idx);
                        dzs.push(preDz);
                        repeated.insertAdjacentElement("beforebegin", preDz.element);
                    }
                    minChildIdx = idx! + 1;
                }
                // If the next element is an explicit DZ for this slot, don't add an implicit final DZ.
                if (!isOldStyle && (!explicitDzs.has(name) || element.nextElementSibling !== explicitDzs.get(name))) {
                    const endDz = mkDropZone(name, minChildIdx);
                    dzs.push(endDz);
                    element.insertAdjacentElement("beforebegin", endDz.element);
                }
            }
        }

        return dzs;
    },

    disableDropMode(this: Component) {
        const self = this;
        self._anvil.element.find(".anvil-force-show-dz").removeClass("anvil-force-show-dz");
        for (const el of self._anvil.element.find("[anvil-html-template-dropzone]")) {
            el.remove();
        }
    },

    getContainerDesignInfo(forChild: Component) {
        return {
            layoutPropertyDescriptions: [
                {
                    name: "slot",
                    description: "The HTML slot into which this component will be dropped",
                    type: "string",
                    isLayoutProperty: true,
                },
            ],
        };
    },

    updateLayoutProperties(this: Component, forChild, { slot }) {
        const self = this;
        return chainOrSuspend(
            pyCallOrSuspend(Sk.abstr.gattr(forChild, s_remove_from_parent), []),
            () => pyCallOrSuspend(Sk.abstr.gattr(self, s_add_component), [forChild], ["slot", new pyStr(slot)]),
            () => ({ slot })
        );
    },
};

export const DesignHtmlTemplateV3 = Sk.misceval.buildClass(
    BASE_MODULES["anvil"],
    function ($gbl, $loc) {
        $loc.anvil$hookSpec = Object.assign(Object.create(HtmlTemplate.prototype.anvil$hookSpec), DesignerHooks);
    },
    "DesignHtmlTemplateV3",
    [DesignHtmlTemplate]
) as ComponentConstructor;
